import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { OperatorAdminService } from '../../services/operator-admin/operator-admin.service';
import { ToastService } from '../../services/toast/toast.service';
import { PermissionService } from '../../services/permission/permission.service';
import { LoaderComponent } from '../../atoms/loader/loader.component';
import { OaAddOperatorDialogComponent } from './oa-add-operator-dialog/oa-add-operator-dialog.component';
import { OaPermissionsDialogComponent } from './oa-permissions-dialog/oa-permissions-dialog.component';

@Component({
  selector: 'app-oa-operators',
  standalone: true,
  imports: [CommonModule, FormsModule, LoaderComponent],
  templateUrl: './oa-operators.component.html',
  styleUrl: './oa-operators.component.scss',
})
export class OaOperatorsComponent implements OnInit {
  operators: any[] = [];
  isLoading: boolean = false;
  canManage: boolean = false;
  searchText: string = '';

  // activity report (all operators)
  activity: any[] = [];
  activityLoading: boolean = false;
  activityLoaded: boolean = false;
  activityFrom: string = '';
  activityTo: string = '';
  activityOperator: string = '';
  // client-side narrowing over the loaded rows
  activityCategory: string = '';
  activityAction: string = '';

  constructor(
    private operatorAdminService: OperatorAdminService,
    private toastService: ToastService,
    private permissionService: PermissionService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.canManage = this.permissionService.can('operator-management', 'can_manage_users');

    // default the activity window to the last 7 days
    const day = 24 * 60 * 60 * 1000;
    this.activityTo = this.toDateInput(new Date());
    this.activityFrom = this.toDateInput(new Date(Date.now() - 6 * day));

    this.load();
  }

  private toDateInput(d: Date): string {
    return d.toLocaleDateString('en-CA');
  }

  load(): void {
    this.isLoading = true;
    this.operatorAdminService.listOperators().subscribe({
      next: (res: any) => {
        this.operators = res?.data?.operators || [];
        this.isLoading = false;
      },
      error: (err) => {
        this.operators = [];
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to load operators.');
      },
    });
  }

  // client-side filter over name / username / email / mobile / roles
  get filteredOperators(): any[] {
    const q = this.searchText.trim().toLowerCase();
    if (!q) return this.operators;
    return this.operators.filter((op) =>
      [op.fullName, op.userName, op.emailId, op.mobileNumber, op.roles]
        .some((v) => (v || '').toString().toLowerCase().includes(q))
    );
  }

  addOperator(): void {
    const ref = this.dialog.open(OaAddOperatorDialogComponent, { width: '640px', maxWidth: '95vw' });
    ref.afterClosed().subscribe((changed: boolean) => {
      if (changed) this.load();
    });
  }

  editOperator(op: any): void {
    const ref = this.dialog.open(OaAddOperatorDialogComponent, {
      width: '640px',
      maxWidth: '95vw',
      data: { operator: op },
    });
    ref.afterClosed().subscribe((changed: boolean) => {
      if (changed) this.load();
    });
  }

  toggleRestricted(op: any): void {
    this.isLoading = true;
    this.operatorAdminService.setOperatorRestricted(op.sumId, !op.isRestricted).subscribe({
      next: (res: any) => {
        this.toastService.success(res?.message || 'Updated.');
        this.load();
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to update operator.');
      },
    });
  }

  managePermissions(op: any): void {
    this.dialog.open(OaPermissionsDialogComponent, {
      width: '760px',
      maxWidth: '95vw',
      data: { sumId: op.sumId, name: op.fullName },
    });
  }

  loadActivity(): void {
    this.activityLoading = true;
    this.operatorAdminService
      .getActivity({
        fromDate: this.activityFrom,
        toDate: this.activityTo,
        operatorName: this.activityOperator || undefined,
      })
      .subscribe({
        next: (res: any) => {
          this.activity = res?.data?.activity || [];
          this.activityLoaded = true;
          this.activityLoading = false;
        },
        error: (err) => {
          this.activity = [];
          this.activityLoaded = true;
          this.activityLoading = false;
          this.toastService.error(err?.error?.message || 'Failed to load activity.');
        },
      });
  }

  activityKey(row: any): string {
    return String(row?.id ?? '');
  }

  get activityCategories(): string[] {
    return Array.from(new Set(this.activity.map((r) => r.category).filter(Boolean))).sort();
  }

  get activityActions(): string[] {
    return Array.from(new Set(this.activity.map((r) => r.action).filter(Boolean))).sort();
  }

  get filteredActivity(): any[] {
    return this.activity.filter(
      (r) =>
        (!this.activityCategory || r.category === this.activityCategory) &&
        (!this.activityAction || r.action === this.activityAction)
    );
  }

  activityTime(row: any): string {
    if (!row?.time) return '-';
    const d = new Date(row.time);
    if (isNaN(d.getTime())) return String(row.time);
    return d.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
  }

  // a short human reference from the request payload (booking id when present)
  activityRef(row: any): string {
    const details = row?.details;
    if (details && typeof details === 'object' && details.requisitionId) {
      return `Booking #${details.requisitionId}`;
    }
    return '-';
  }
}
